import dot from 'dot-prop-immutable';
import { nanoid } from 'nanoid';

import {
  getAllSupplierMaterials,
  getMaterialClassEntities,
  getMaterialFamilyEntities,
  getMaterialEntities,
} from 'store/materials/reducer';

import { types } from './actions';

const initialState = {
  actionModalType: null,
  expandedClasses: [],
  expandedFamilies: [],
  expandedMaterials: [],
  expandForSearchRequest: null,
  isActionModalOpen: false,
  isExcludedMaterialDrawerOpen: {
    materialClass: false,
    materialFamily: false,
    material: false,
  },
  isUpdatingSelectorSettings: false,
  isUploadingCsv: false,
};

const reducer = (state = initialState, action) => {
  const { payload, type } = action;
  switch (type) {
    case types.ACTION_MODAL_OPEN:
      return {
        ...state,
        actionModalType: payload.type,
        isActionModalOpen: true,
      };
    case types.ACTION_MODAL_CLOSE:
      return {
        ...state,
        actionModalType: null,
        isActionModalOpen: false,
      };
    case types.EXPAND_FOR_SEARCH:
      return {
        ...state,
        expandedClasses: [payload.classId],
        expandedFamilies: [payload.familyId],
        expandedMaterials: [payload.materialId],
        expandForSearchRequest: nanoid(),
      };
    case types.RECEIVE_EXPANDED_CLASSES:
      return {
        ...state,
        expandedClasses: payload.classIds,
      };
    case types.RECEIVE_EXPANDED_FAMILIES:
      return {
        ...state,
        expandedFamilies: payload.familyIds,
      };
    case types.RECEIVE_EXPANDED_MATERIALS:
      return {
        ...state,
        expandedMaterials: payload.materialIds,
      };
    case types.UPLOAD_SUPPLIER_MATERIALS_REQUEST:
      return {
        ...state,
        isUploadingCsv: true,
      };
    case types.UPLOAD_SUPPLIER_MATERIALS_SUCCESS:
      return {
        ...state,
        expandedClasses: [],
        expandedFamilies: [],
        expandedMaterials: [],
        expandForSearchRequest: null,
        isUploadingCsv: false,
      };
    case types.UPLOAD_SUPPLIER_MATERIALS_ERROR:
      return {
        ...state,
        isUploadingCsv: false,
      };
    case types.UPDATE_MATERIAL_SELECTOR_SETTINGS_REQUEST:
      return {
        ...state,
        isUpdatingSelectorSettings: true,
      };
    case types.UPDATE_MATERIAL_SELECTOR_SETTINGS_ERROR:
    case types.UPDATE_MATERIAL_SELECTOR_SETTINGS_SUCCESS:
      return {
        ...state,
        isUpdatingSelectorSettings: false,
      };
    case types.EXCLUDED_MATERIAL_SELECTOR_DRAW_OPEN:
      return dot.set(
        state,
        `isExcludedMaterialDrawerOpen.${payload.entityType}`,
        true,
      );
    case types.EXCLUDED_MATERIAL_SELECTOR_DRAW_CLOSE:
      return dot.set(
        state,
        `isExcludedMaterialDrawerOpen.${payload.entityType}`,
        false,
      );
    case types.EXCLUDED_MATERIAL_SELECTOR_DRAW_CLOSE_ALL:
      return {
        ...state,
        isExcludedMaterialDrawerOpen: {
          ...initialState.isExcludedMaterialDrawerOpen,
        },
      };
    default:
      return state;
  }
};

export default reducer;

const getScope = (state) => state.modules.materialListPage;

export const getExpandedClasses = (state) => getScope(state).expandedClasses;

export const getExpandedFamilies = (state) => getScope(state).expandedFamilies;

export const getExpandedMaterials = (state) =>
  getScope(state).expandedMaterials;

export const getMaterialIdToSupplierMaterials = (state) => {
  const supplierMaterials = getAllSupplierMaterials(state);
  const materialIdToSupplierMaterials = {};
  for (const supplierMaterial of supplierMaterials) {
    if (supplierMaterial.materialId in materialIdToSupplierMaterials) {
      materialIdToSupplierMaterials[supplierMaterial.materialId].push(
        supplierMaterial,
      );
    } else {
      materialIdToSupplierMaterials[supplierMaterial.materialId] = [
        supplierMaterial,
      ];
    }
  }
  return materialIdToSupplierMaterials;
};

export const getMaterialsAutoCompleteDataSource = (state) => {
  const classes = getMaterialClassEntities(state);
  const families = getMaterialFamilyEntities(state);
  const materials = getMaterialEntities(state);
  const matIdToSupplierMaterials = getMaterialIdToSupplierMaterials(state);

  const ret = [];
  for (const key in materials) {
    const material = materials[key];
    const mFamily = families[material.materialFamilyId];
    const mClass = classes[mFamily.materialClassId];
    const relatedSupplierMaterials = matIdToSupplierMaterials[material.id];
    if (relatedSupplierMaterials) {
      if (relatedSupplierMaterials.length === 1) {
        ret.push({
          classId: mClass.id,
          familyId: mFamily.id,
          materialId: material.id,
          supplierMaterialId: relatedSupplierMaterials.id,
          className: mClass.name,
          display: `${mClass.name} / ${mFamily.name} / ${relatedSupplierMaterials[0].displayName}`,
        });
      } else {
        for (const supplierMaterial of relatedSupplierMaterials) {
          ret.push({
            classId: mClass.id,
            familyId: mFamily.id,
            materialId: material.id,
            supplierMaterialId: supplierMaterial.id,
            className: mClass.name,
            display: `${mClass.name} / ${mFamily.name} / ${material.name} / ${supplierMaterial.displayName}`,
          });
        }
      }
    } else {
      ret.push({
        classId: mClass.id,
        familyId: mFamily.id,
        materialId: material.id,
        supplierMaterialId: null,
        className: mClass.name,
        display: `${mClass.name} / ${mFamily.name} / ${material.name}`,
      });
    }
  }

  // keep additive at the bottom of the list
  ret.sort((a, b) => {
    if (a.className === 'Additive' && b.className === 'Additive') {
      return a.display.localeCompare(b.display);
    } else if (a.className !== 'Additive' && b.className !== 'Additive') {
      return a.display.localeCompare(b.display);
    } else {
      if (a.className === 'Additive') return 1;
      else return -1;
    }
  });
  return ret;
};

export const getExpandForSearchRequest = (state) =>
  getScope(state).expandForSearchRequest;

export const getIsActionModalOpen = (state) =>
  getScope(state).isActionModalOpen;

export const getActionModalType = (state) => getScope(state).actionModalType;

export const getIsUploadingCsv = (state) => getScope(state).isUploadingCsv;

export const getIsUpdatingSelectorSettings = (state) =>
  getScope(state).isUpdatingSelectorSettings;

export const getIsExcludedMaterialDrawerOpen = (state, entityType) =>
  getScope(state).isExcludedMaterialDrawerOpen[entityType];
